import React, { useState } from 'react';
import '../styles/PopupForm.css';
import { trackEvent } from '../utils/analytics';
import { API_ENDPOINTS } from '../config/constants';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';

function PopupForm({ isOpen, onClose, formType, sourceKey, interestKey }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    consent: false
  });

  const descriptions = {
    live_session: {
      title: "Join Next Live Session",
      description: "Get ready to master GitHub Copilot in our interactive live session. Limited seats available for personalized attention."
    },
    course_outline: {
      title: "Download Course Outline",
      description: "Get detailed insights into our comprehensive GitHub Copilot training curriculum and learning path."
    },
    schedule_call: {
      title: "Schedule a Call",
      description: "Book a personalized call with our architect mentor to discuss your career goals and how we can help you achieve them."
    },
    curriculum: {
      title: "Download Curriculum",
      description: "Get detailed insights into our comprehensive architect program curriculum and learning path."
    },
    mentor_connect: {
      title: "Connect with Mentor",
      description: "Get personalized guidance from our experienced architect mentor to accelerate your career growth."
    },
    save_spot: {
      title: "Save Your Spot",
      description: "Reserve your place in our upcoming batch. Limited seats available to ensure personalized attention."
    },
    register_now: {
      title: "Register Now",
      description: "Join our comprehensive training program and accelerate your career growth with expert guidance."
    }
  };

  // Map form types to interests
  const getInterestFromFormType = (type) => {
    switch(type) {
      case 'live_session':
        return 'copilot_training';
      case 'course_outline':
        return 'training_info';
      case 'schedule_call':
        return 'architect_consultation';
      case 'curriculum':
        return 'architect_program';
      case 'mentor_connect':
        return 'mentorship';
      case 'save_spot':
        return 'program_registration';
      case 'register_now':
        return 'program_registration';
      default:
        return 'general';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(API_ENDPOINTS.LEADS_SUBMIT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        },
        body: JSON.stringify({
          ...formData,
          source: sourceKey || formType,
          interestedIn: interestKey || getInterestFromFormType(formType)
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      trackEvent({
        event: 'form_submit',
        category: 'Lead',
        label: `popup_${formType}_submit`,
        action: 'submit',
        formData: {
          hasName: !!formData.name,
          hasEmail: !!formData.email,
          hasMobile: !!formData.mobile,
          hasConsent: formData.consent
        }
      });

      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
      // Could add error handling UI here
    }
  };

  const handleClose = () => {
    trackEvent({
      event: 'popup_close',
      category: 'Engagement',
      label: `popup_${formType}_close`,
      action: 'close',
      formData: {
        hasName: !!formData.name,
        hasEmail: !!formData.email
      }
    });
    onClose();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    
    setFormData({
      ...formData,
      [name]: fieldValue
    });
    
    // Track when user starts typing (not for checkboxes)
    if (type !== 'checkbox' && value && value.length === 1) {
      trackEvent({
        event: 'form_field_interaction',
        category: 'Form',
        label: `popup_${formType}_field_${name}`,
        action: 'start_typing'
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <button className="popup-close" onClick={handleClose}>&times;</button>
        <h2>{descriptions[formType].title}</h2>
        <p className="popup-description">{descriptions[formType].description}</p>
        
        <form onSubmit={handleSubmit} className="popup-form">
          <div className="form-group">
            <input
              type="text"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <input
              type="email"
              name="email"
              placeholder="Your Email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <input
              type="tel"
              name="mobile"
              placeholder="Mobile Number"
              value={formData.mobile}
              onChange={handleChange}
              required
              pattern="[0-9]{10,15}"
              title="Please enter a valid mobile number (10-15 digits)"
            />
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', textAlign: 'left' }}>
            <input
              type="checkbox"
              name="consent"
              id={`consent-${formType}`}
              checked={formData.consent}
              onChange={handleChange}
              required
              style={{ marginTop: '4px', width: 'auto', minWidth: '16px', flexShrink: 0 }}
            />
            <label htmlFor={`consent-${formType}`} style={{ fontSize: '0.9rem', lineHeight: '1.4', cursor: 'pointer' }}>
              I allow to send me regular updates
            </label>
          </div>
          <button type="submit" className="submit-button">
            {formType === 'live_session' ? 'Reserve My Seat' : 'Send Me the Outline'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default PopupForm;